import { InfoCircleOutlined } from "@ant-design/icons";
import {
  Button,
  Checkbox,
  Col,
  DatePicker,
  List,
  Progress,
  Row,
  Select,
  Space,
  notification,
} from "antd";
import dayjs from "dayjs";
import { memo, useEffect, useState } from "react";

import Logout from "../components/Logout.js";
import "./Header.css";

const { RangePicker } = DatePicker;

const TimeSettings = memo(
  ({
    from,
    setFrom,
    to,
    setTo,
    selectedDays,
    setSelectedDays,
    selectedHours,
    setSelectedHours,
  }) => {
    const daysOfWeek = [
      { label: "Lu", value: 1 },
      { label: "Ma", value: 2 },
      { label: "Me", value: 3 },
      { label: "Je", value: 4 },
      { label: "Ve", value: 5 },
      { label: "Sa", value: 6 },
      { label: "Di", value: 0 },
    ];

    const hoursOfDay = [...Array(24).keys()].map((x) => {
      return { label: `${x}-${x + 1}h`, value: x };
    });

    const [checkedDays, setCheckedDays] = useState(selectedDays);
    const [checkedHours, setCheckedHours] = useState(selectedHours);

    const [isUserInteracting, setIsUserInteracting] = useState(false);

    const handleRangePickerChange = (dates, dateString) => {
      if (dates) {
        setFrom(
          dates[0].set("hour", 0).set("minute", 0).set("second", 0).format(),
        );
        setTo(
          dates[1].set("hour", 23).set("minute", 59).set("second", 59).format(),
        );
      }
    };

    notification.config({
      maxCount: 1,
    });

    const [notificationApi, notificationContextHolder] =
      notification.useNotification();

    useEffect(() => {
      const timeoutId = setTimeout(() => {
        setSelectedDays(checkedDays); // this goes upstream to the parent component
      }, 500);
      return () => clearTimeout(timeoutId);
    }, [checkedDays, setSelectedDays]);

    useEffect(() => {
      const timeoutId = setTimeout(() => {
        setSelectedHours(checkedHours); // this goes upstream to the parent component
      }, 500);
      return () => clearTimeout(timeoutId);
    }, [checkedHours, setSelectedHours]);

    useEffect(() => {
      if (isUserInteracting || checkedDays.length === 0) {
        return;
      }

      const _to = dayjs(to);
      const _from = dayjs(from);

      if (_to.diff(_from, "week") < 1) {
        const daysInRange = new Set([_from.day()]); // .day() outputs the day of week starting from 0 = Sunday

        for (let i = 1; i < 7; i++) {
          const d = _from.add(i, "day");

          if (d > _to) {
            break;
          }

          daysInRange.add(d.day());
        }

        if (daysInRange.intersection(new Set(checkedDays)).size === 0) {
          notificationApi.warning({
            message: "Attention",
            description:
              "La plage temporelle choisie ne contient aucun des jours de la semaine sélectionnés.",
            placement: "top",
            role: "alert",
            duration: 0, // stays open forever
            key: "bad-time-settings",
          });
        } else {
          notificationApi.destroy("bad-time-settings");
        }
      } else {
        notificationApi.destroy("bad-time-settings");
      }
    }, [from, to, checkedDays, isUserInteracting, notificationApi]);

    const checkAllDays = (e) => {
      setCheckedDays(e.target.checked ? daysOfWeek.map((x) => x.value) : []);
    };

    const checkAllHours = (e) => {
      setCheckedHours(e.target.checked ? hoursOfDay.map((x) => x.value) : []);
    };

    const data = [
      {
        key: "timerange",
        title: "Plage temporelle",
        content: (
          <RangePicker
            onChange={handleRangePickerChange}
            value={[dayjs(from, "YYYY-MM-DD"), dayjs(to, "YYYY-MM-DD")]}
            allowClear={false}
            style={{ width: "100%" }}
            onOpenChange={(isOpen) => setIsUserInteracting(isOpen)}
          />
        ),
      },
      {
        key: "days-of-week",
        title: "Jours de la semaine",
        content: (
          <Row span={24} align="middle" justify="space-between">
            <Col span={18}>
              <Select
                size="medium"
                mode="multiple"
                allowClear
                style={{ width: "100%" }}
                maxTagCount="responsive"
                onChange={setCheckedDays}
                options={daysOfWeek}
                value={checkedDays}
                optionFilterProp="label"
                onOpenChange={(isOpen) => setIsUserInteracting(isOpen)}
              />
            </Col>
            <Col span={5} offset={1}>
              <Checkbox
                indeterminate={
                  checkedDays.length && checkedDays.length < daysOfWeek.length
                }
                checked={checkedDays.length === daysOfWeek.length}
                onChange={checkAllDays}
              >
                Tous
              </Checkbox>
            </Col>
          </Row>
        ),
      },
      {
        key: "hours-of-day",
        title: "Heures du jour",
        content: (
          <Row span={24} align="middle" justify="space-between">
            <Col span={18}>
              <Select
                size="medium"
                mode="multiple"
                allowClear
                style={{ width: "100%" }}
                maxTagCount="responsive"
                onChange={setCheckedHours}
                options={hoursOfDay}
                value={checkedHours}
                optionFilterProp="label"
              />
            </Col>
            <Col span={5} offset={1}>
              <Checkbox
                indeterminate={
                  checkedHours.length && checkedHours.length < hoursOfDay.length
                }
                checked={checkedHours.length === hoursOfDay.length}
                onChange={checkAllHours}
              >
                Toutes
              </Checkbox>
            </Col>
          </Row>
        ),
      },
    ];

    return (
      <div className="time-settings">
        {notificationContextHolder}
        <List
          size="small"
          dataSource={data}
          rowKey="key"
          grid={{ column: 3 }}
          renderItem={(item) => (
            <List.Item>
              <List.Item.Meta
                key={item.key}
                title={item.title}
                description={item.content}
              />
            </List.Item>
          )}
        />
      </div>
    );
  },
);

const DataQuality = memo(({ completenessRate }) => {
  const completenessPercent = Math.round(completenessRate * 100);

  const data = [
    {
      key: "data-completeness",
      title: "Complétude des données",
      content: (
        <Progress
          percent={completenessPercent}
          format={(percent) => (
            <span style={{ color: "black" }}>{percent} %</span>
          )}
          style={{ marginTop: "3px" }}
          strokeColor="#1677ff"
        />
      ),
    },
  ];

  return (
    <div className="data-quality">
      <List
        size="small"
        dataSource={data}
        rowKey="key"
        grid={{ column: 1 }}
        renderItem={(item) => (
          <List.Item>
            <List.Item.Meta
              key={item.key}
              title={item.title}
              description={item.content}
            />
          </List.Item>
        )}
      />
    </div>
  );
});

const Header = memo(
  ({
    setIsModalOpen,
    setToken,
    from,
    setFrom,
    to,
    setTo,
    selectedDays,
    setSelectedDays,
    selectedHours,
    setSelectedHours,
    completenessRate,
  }) => {
    const showModal = () => {
      setIsModalOpen(true);
    };

    return (
      <div className="header">
        <h2>Waze Data Explorer</h2>
        <TimeSettings
          from={from}
          setFrom={setFrom}
          to={to}
          setTo={setTo}
          selectedDays={selectedDays}
          setSelectedDays={setSelectedDays}
          selectedHours={selectedHours}
          setSelectedHours={setSelectedHours}
        />
        <DataQuality completenessRate={completenessRate} />
        <Space>
          <Button
            type="default"
            onClick={showModal}
            icon={<InfoCircleOutlined />}
          >
            À propos
          </Button>
          <Logout setToken={setToken} />
        </Space>
      </div>
    );
  },
);

export default Header;
